import Styles from './NodeSQL.module.scss';
import { useState, useEffect, useRef, React, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import { ToastContainer, toast } from 'react-toastify';
import {
    MoreHorizSharp,
    MoreVertSharp,
    Menu,
    KeyboardDoubleArrowRight,
    Visibility,
    ChevronRight,
    KeyboardArrowDown,
    PlayArrow,
} from '@mui/icons-material';


// module imports
import {
    Collapse, Typography, Fab,
    Tooltip,
    IconButton,
} from '@mui/material'
import MonacoEditor from '@monaco-editor/react';

// eslint-disable-next-line import/no-webpack-loader-syntax
import sqlWasm from '!!file-loader?name=sql-wasm-[contenthash].wasm!sql.js/dist/sql-wasm.wasm';
import initSqlJs from "sql.js";
import axios from 'axios';
import { Python3Parser } from 'dt-python-parser';
import _ from 'lodash';

// component imports
import InfoDrawer from 'components/global/NodeLayout/InfoDrawer';
import { HelperChat } from 'components/global/HelperChat/HelperChat';
import { BackConfirmationCard } from 'components/global/BackConfirmationCard/BackConfirmationCard';
import { ConfirmationCard } from 'components/global/ConfirmationCard/ConfirmationCard';
import { LightTooltip } from 'components/global/NodeLayout/NodeTopostman';
import { LoadingBackdrop } from 'components/global/LoadingBackdrop/LoadingBackdrop';
import { Joyride } from 'components/walkthrough/walkthrough';
import { useAPI, AuthContext, Feedback, ViewContext, useCan } from 'components/lib';
import RevealAnswerKey from 'components/RevealAnswerKey/RevealAnswerKey';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { FaDatabase, FaTable } from 'react-icons/fa';
// Utils import.
import { decoder } from 'utils/helpers/decoder';

// assets import.
import failedAttemptSound from 'assets/sounds/try-again.mp3';
import { AivagamButton } from 'components/global/AivagamButton/AivagamButton';
import { StepsDrawer } from 'views/CodeEditor/components';
import { InstructionsPane } from '../NodeJSONata/component/InstructionsPane/InstructionsPane';
import NodeProgress from 'components/global/NodeLayout/NodeProgress/NodeProgress';
import { OutputPane } from './component/OutputPane';
const finalAudio = `${process.env.REACT_APP_GLOBAL_MEDIA_URL}/media/git/voice/finish_node.mp3`;


export function NodeSQL(props) {

    const navigate = useNavigate();
    const parser = new Python3Parser();
    const { node_id } = useParams();
    const { lab_id } = useParams();
    const { resource } = useParams();
    const { data, loading } = useAPI(`/api/labs/${lab_id}/nodes/${node_id}/${resource}`, 'GET');
    const context = useContext(AuthContext);

    const [currentStep, setCurrentStep] = useState(0);
    const [nodeFinished, setNodeFinished] = useState(false);
    const [steps, setSteps] = useState();
    const [nodeInfo, setNodeInfo] = useState();
    const [isMuted, setIsMuted] = useState(true);
    const [isExecuted, setIsExecuted] = useState(false);
    const [startLab, setStartLab] = useState(true);
    const [drawerOpen, setDrawerOpen] = useState(false);

    const [db, setDb] = useState(null);
    // eslint-disable-next-line no-unused-vars
    const [error, setError] = useState(null);
    const [results, setResults] = useState([]);
    const [query, setQuery] = useState("");
    const [tableInfo, setTableInfo] = useState([]);
    const [triggerInfo, setTriggerInfo] = useState([]);
    const [showTables, setShowTables] = useState(false);
    const [expandedTables, setExpandedTables] = useState({});
    const [sessionId, setSessionId] = useState('');
    const [instructOpen, setInstructOpen] = useState(true);
    const [outputOpen, setOutputOpen] = useState(true);
    const [showOutput, setShowOutput] = useState(false);
    const [showStepsDrawer, setShowStepsDrawer] = useState(false)
    const [showFinishConfirm, setShowFinishConfirm] = useState(false);
    const [showReveal, setShowReveal] = useState(false);
    const roundedPercentage = nodeFinished ? 100 : Math.ceil(currentStep / nodeInfo?.numOfSteps * 100);

    const isSupervisor = useCan('node.progress.flag');
    const isAssessmentNode = nodeInfo?.is_assessment;
    const canReveal = !!(isAssessmentNode && isSupervisor);

    const wastedAttemptAudio = new Audio(failedAttemptSound);

    const [maxUnlocked, setMaxUnlocked] = useState(0);
    const audioRef = useRef(null);
    const instructPanelRef = useRef(null)
    const finalAudioRef = useRef(null);
    const viewContext = useContext(ViewContext)
    const handleStepClick = (index) => {
        setCurrentStep(index);
        setShowStepsDrawer(false)
    };


    useEffect(() => {
        setMaxUnlocked(prev => Math.max(prev, currentStep));
    }, [currentStep]);

    useEffect(() => {
        async function initializeDatabase() {
            try {
                const response = await axios.get('/api/sampledb');
                const buffer = response.data.sample_db.data;
                const SQL = await initSqlJs({ locateFile: () => sqlWasm });
                setDb(new SQL.Database(new Uint8Array(buffer)));
            } catch (err) {
                console.error("Error initializing database:", err);
                setError(err);
            }
        }

        initializeDatabase();
    }, []);

    async function getTableInfo(db) {
        const tablesQuery = "SELECT name FROM sqlite_master WHERE type='table';";
        const tablesResult = db.exec(tablesQuery);

        const tableInfo = [];
        for (const table of tablesResult[0]?.values || []) {
            const tableName = table[0];
            const fieldsQuery = `PRAGMA table_info(${tableName});`;
            const fieldsResult = db.exec(fieldsQuery);

            const fields = fieldsResult[0]?.values.map(field => {
                return {
                    name: field[1],  // Column name
                    type: field[2],  // Column type
                };
            }) || [];
            tableInfo.push({ tableName, fields });
        }

        return tableInfo;
    }

    async function getTriggerInfo(db) {

        const triggerQuery = "SELECT name FROM sqlite_master WHERE type='trigger';";
        const triggerResult = db.exec(triggerQuery);

        const triggerInfo = [];
        for (const trigger of triggerResult[0]?.values || []) {
            triggerInfo.push(trigger[0]);
        }
        return triggerInfo;
    }

    useEffect(() => {
        if (db) {
            getTableInfo(db)
                .then(info => setTableInfo(info))
                .catch(err => setError(err));
        }
    }, [db]);

    useEffect(() => {
        if (db) {
            getTriggerInfo(db)
                .then(info => setTriggerInfo(info))
                .catch(err => setError(err));
        }
    }, [db]);

    useEffect(() => {
        if (data) {
            const jsonData = decoder(data);
            setNodeInfo(jsonData);
            setSteps(jsonData?.steps?.steps);
            jsonData.numOfSteps = jsonData.steps.steps.length;
            document.title = props.t('Labs.Nodes.NodeSQL.title', { nodeName: jsonData.name });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    useEffect(() => {
        if (!steps) return;

        if (audioRef.current) {
            audioRef.current.pause();
        }

        const audioLink = `${process.env.REACT_APP_GLOBAL_MEDIA_URL}${steps[currentStep]?.voice}`;
        if (audioLink && !isMuted) {
            const newAudio = new Audio(audioLink);
            audioRef.current = newAudio;
            newAudio.play();
        }

        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStep, isMuted]);

    useEffect(() => {
        if (!nodeFinished) return;

        if (audioRef.current) {
            audioRef.current.pause();
        }

        const audio = new Audio(finalAudio);
        finalAudioRef.current = audio;
        audio.play();

        viewContext.notification.show(
            props.t('Labs.Nodes.NodeSQL.end'),
            'info',
            false
        );

        return () => {
            if (finalAudioRef.current) {
                finalAudioRef.current.pause();
            }
        };

    }, [nodeFinished])



    function handleIncorrectAttempt(errorMessage) {
        const message = errorMessage || props.t('Labs.Nodes.NodeSQL.toast.defaultError');
        wastedAttemptAudio.play();
        toast.dark(`${message}`, {
            type: 'error',
            position: 'top-right',
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
        });
    };

    async function exec() {
        try {
            setResults(db.exec(query));
            setError(null);
            const updatedTableInfo = await getTableInfo(db);
            setTableInfo(updatedTableInfo);
            const updatedTriggerInfo = await getTriggerInfo(db);
            setTriggerInfo(updatedTriggerInfo);
            setShowOutput(true);
            setIsExecuted(true);

        } catch (err) {
            handleIncorrectAttempt(err)
            const updatedTableInfo = await getTableInfo(db);
            setTableInfo(updatedTableInfo);
            const updatedTriggerInfo = await getTriggerInfo(db);
            setTriggerInfo(updatedTriggerInfo);
            setError(err);
            setResults([]);
            setShowOutput(true);
            setIsExecuted(true);
        }
    };

    //Tokenizing the user input and expected input
    const inputCompare = (input) => {
        // \r\n is package specific as the parser expects it at each line break
        const userCodeTokens = parser.getAllTokens(input + '\r\n');
        // Filtering out only the tokens by removing spaces and the \r\n after parsing
        const userTokensArray = userCodeTokens
            .map((obj) => obj['text'])
            .filter((text) => text.trim() !== '' && text !== '\r\n')
            .map(token => token.toLowerCase());

        const expectedTokens = parser.getAllTokens(steps[currentStep].exp_input + '\r\n');
        const expectedTokensArray = expectedTokens
            .map((obj) => obj['text'])
            .filter((text) => text.trim() !== '' && text !== '\r\n')
            .map(token => token.toLowerCase());

        //Comparing user code tokens against expected code tokens
        return _.isEqual(expectedTokensArray, userTokensArray);
    };

    const moveToNext = () => {
        if (currentStep === nodeInfo.numOfSteps - 1) {
            if (maxUnlocked === nodeInfo.numOfSteps - 1) setMaxUnlocked(prev => prev + 1)
            setNodeFinished(true);
            return;
        }
        setCurrentStep(currentStep + 1);
    };


    const checkAndProceed = async (userInput) => {
        const isInputCorrect = inputCompare(userInput);
        if (isInputCorrect) {
            moveToNext();
            setIsExecuted(false);
            clearEditorCode();
        } else {
            handleIncorrectAttempt();
        }

    };
    const toggleTableExpand = (tableName) => {
        setExpandedTables(prev => ({
            ...prev,
            [tableName]: !prev[tableName]
        }));
    };
    const clearEditorCode = () => {
        if (('clear' in steps[currentStep] && steps[currentStep].clear === 'true') || !('clear' in steps[currentStep])) {
            setQuery('');
        }
    };

    const handleVerify = async () => {
        await checkAndProceed(query);
    };


    const handleFinish = () => {
        const options = {
            method: 'POST',
            url: `/api/user/labs/${lab_id}/nodes/${node_id}/end`,
            headers: {
                accept: 'application/json',
            },
            data: {
                ...(nodeInfo.is_assessment === 1 && { completionPercent: roundedPercentage }),
            },
        };
        axios
            .request(options)
            .then(async function (response) {
                await new Promise((resolve) => setTimeout(resolve, 50)); // Adding a wait for api post to happen
                if (nodeInfo?.is_assessment) {
                    navigate(`/labs/${lab_id}/nodes`);
                } else {
                    navigate(`/labs/${lab_id}/nodes/${node_id}/quiz/attempt`);
                }
            })
            .catch((err) => {
                navigate('/oops');
            });
    };

    const resetWalkthrough = (data) => {
        if (data.status === 'finished' | data.action === 'skip' | data.action === 'tour:end') {
            setIsMuted(!isMuted)
        }
    };

    useEffect(() => {
        const resourceIncluded = context?.user?.stop_walkthrough?.includes(nodeInfo?.resource?.lab_resource_id);
        if (resourceIncluded && !startLab) {
            setIsMuted(!isMuted)

        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [context?.user?.stop_walkthrough])

    const showConfirm = () => {
        setShowFinishConfirm(true);
    }

    const handleConfirmClick = () => {
        handleFinish();
    }

    const startLabClick = async () => {
        const options = {
            method: 'POST',
            url: `/api/user/labs/${lab_id}/nodes/${node_id}/start`,
            headers: {
                accept: 'application/json',
            },
        };
        await axios
            .request(options)
            .then((response) => {
                new Promise((resolve) => setTimeout(resolve, 100));
                setStartLab(false);
                if (context?.user?.stop_walkthrough?.includes(nodeInfo?.resource?.lab_resource_id) || !!nodeInfo.is_assessment) {
                    setIsMuted(!isMuted)
                }
                setSessionId(response.data.session_id);
            })
            .catch((err) => {
                if (err.response.status === 403) {
                    navigate('/nopermission');
                } else {
                    navigate('/oops');
                }
            });
    };

    const handleVoiceClick = () => {
        setIsMuted(!isMuted);
    };

    return (
        <>
            {
                loading ? (
                    <div>
                        <LoadingBackdrop isOpen={true} />
                    </div >
                ) : steps ? (
                    <>
                        <ToastContainer
                            id="warning-toast"
                            position="top-right"
                            autoClose={10000}
                            hideProgressBar={false}
                            newestOnTop
                            closeOnClick
                            pauseOnFocusLoss
                            draggable
                            pauseOnHover
                            limit={4}
                        />
                        <BackConfirmationCard
                            isSaveAvailable={false}
                        />
                        <div id="ancestorContainer" className={Styles.ancestorContainer}>
                            <ConfirmationCard
                                title={
                                    nodeInfo.is_assessment ?
                                        props.t("Labs.Nodes.NodeSQL.confirm.start.assessment.title")
                                        : props.t("Labs.Nodes.NodeSQL.confirm.start.default.title")
                                }
                                body={
                                    nodeInfo.is_assessment ?
                                        props.t("Labs.Nodes.NodeSQL.confirm.start.assessment.body")
                                        : props.t("Labs.Nodes.NodeSQL.confirm.start.default.body")
                                }
                                showCard={startLab}
                                setShowCard={setStartLab}
                                handleCancel={() => { navigate(-1) }}
                                handleConfirm={startLabClick}
                            />
                            {!startLab && nodeInfo.is_assessment !== 1 ? (
                                <Joyride
                                    open={nodeInfo?.resource?.walkthrough?.open}
                                    run={nodeInfo?.resource?.walkthrough?.run}
                                    steps={nodeInfo?.resource?.walkthrough?.steps || []}
                                    resourceId={nodeInfo?.resource?.lab_resource_id}
                                    disableOverlay={true}
                                    callback={resetWalkthrough}
                                />
                            ) : null}
                            <div id="titleContainer" className={Styles.titleContainer}>
                                <div style={{ display: 'flex' }}>
                                    <Tooltip title="Steps" arrow>
                                        <IconButton
                                            id='stepsToggle'
                                            size="large"
                                            edge="start"
                                            color="white"
                                            aria-label="menu-item"
                                            onClick={() => setShowStepsDrawer(true)}
                                            sx={{
                                                transition: 'all 0.3s',
                                            }}
                                        >
                                            <Menu style={{ color: "white" }} />
                                        </IconButton>
                                    </Tooltip>
                                    <StepsDrawer
                                        open={showStepsDrawer}
                                        onClose={() => setShowStepsDrawer(false)}
                                        steps={steps}
                                        onStepClick={handleStepClick}
                                        totalSteps={nodeInfo?.numOfSteps}
                                        currentStep={currentStep}
                                        nodeFinished={nodeFinished}
                                        maxUnlocked={maxUnlocked}
                                    />
                                    <Typography
                                        variant="h3"
                                        sx={{
                                            textTransform: 'uppercase',
                                            color: '#76EAD7',
                                        }}
                                    >
                                        {nodeInfo.name}
                                    </Typography>
                                </div>
                                <span style={{ display: 'flex', flexDirection: 'row' }}>
                                    <LightTooltip
                                        title={
                                            nodeInfo.is_assessment
                                                ? ((roundedPercentage > 70) || nodeFinished ?
                                                    props.t('Labs.Nodes.NodeSQL.tooltip.finish.done')
                                                    : props.t('Labs.Nodes.NodeSQL.tooltip.finish.assessment')
                                                )
                                                : (nodeFinished ?
                                                    props.t('Labs.Nodes.NodeSQL.tooltip.finish.done')
                                                    : props.t('Labs.Nodes.NodeSQL.tooltip.finish.info')
                                                )
                                        }
                                        arrow
                                        placement="left"
                                        classes={{
                                            tooltip: {
                                                backgroundColor: '#fff',
                                                color: 'rgba(0, 0, 0, 0.87)',
                                                boxShadow: '0 2px 5px rgba(0, 0, 0, 0.15)',
                                                fontSize: 11,
                                            },
                                        }}
                                    >
                                        <span>
                                            <button
                                                id="finishButton"
                                                variant="extended"
                                                className={Styles.finishButton}
                                                onClick={() => {
                                                    nodeInfo.is_assessment ? showConfirm() : handleFinish()
                                                }}
                                                disabled={
                                                    props.live_assessment ? false :
                                                        !nodeInfo.is_assessment
                                                            ? !nodeFinished
                                                            : roundedPercentage < 70
                                                }
                                            >
                                                <KeyboardDoubleArrowRight />
                                                {props.t('Labs.Nodes.NodeSQL.button.finish')}
                                            </button>
                                        </span>
                                    </LightTooltip>
                                    <ConfirmationCard
                                        title={
                                            (roundedPercentage > 70 && roundedPercentage < 100) ?
                                                props.t('Labs.Nodes.NodeSQL.confirm.completed.title.warn')
                                                : props.t('Labs.Nodes.NodeSQL.confirm.completed.title.default')
                                        }
                                        body={(roundedPercentage > 70 && roundedPercentage < 100) ?
                                            props.t('Labs.Nodes.NodeSQL.confirm.completed.body.warn')
                                            : props.t('Labs.Nodes.NodeSQL.confirm.completed.body.default')
                                        }
                                        showCard={showFinishConfirm}
                                        setShowCard={setShowFinishConfirm}
                                        handleConfirm={handleConfirmClick}
                                    />

                                    {canReveal && (
                                        <div
                                            id="revealAnswer"
                                            className={Styles.fabContainer}
                                            onClick={() => setShowReveal(true)}
                                            style={{ order: 1, marginRight: 8 }}
                                        >
                                            <LightTooltip
                                                title={props.t('Labs.Nodes.NodeSQL.tooltip.revealAnswer')}
                                                arrow
                                                placement="left"
                                                classes={{
                                                    tooltip: {
                                                        backgroundColor: '#fff',
                                                        color: 'rgba(0, 0, 0, 0.87)',
                                                        boxShadow: '0 2px 5px rgba(0, 0, 0, 0.15)',
                                                        fontSize: 11,
                                                    },
                                                }}
                                            >
                                                <span>
                                                    <Fab color="primary" size="medium">
                                                        <Visibility />
                                                    </Fab>
                                                </span>
                                            </LightTooltip>
                                        </div>
                                    )}

                                    <div id="infoDrawer" className={Styles.fabContainer} style={{ order: 2 }}>
                                        <NodeProgress
                                            lab_id={lab_id}
                                            node_id={node_id}
                                            currentStep={currentStep}
                                            setDrawerOpen={setDrawerOpen}
                                            drawerOpen={drawerOpen}
                                            setCurrentStep={setCurrentStep}
                                            initialUserProgress={nodeInfo.userProgress}
                                            steps={steps}
                                            data={data}
                                            resource={nodeInfo?.resource}
                                            onReveal={() => setShowReveal(true)}
                                            nodeInfo={nodeInfo}
                                            maxUnlocked={maxUnlocked}
                                            setMaxUnlocked={setMaxUnlocked}
                                            sessionId={sessionId}
                                        />
                                        <InfoDrawer
                                            handleDrawer={() => { setDrawerOpen(!drawerOpen) }}
                                            drawerOpen={drawerOpen}
                                            titleText={props.t('Labs.Nodes.NodeSQL.reference', { LabName: nodeInfo.labName })}
                                            displayData={nodeInfo?.content?.reference_data}
                                        />
                                    </div>
                                </span>
                            </div>
                            <PanelGroup direction='horizontal' style={{ height: '100%', width: '100%' }}>
                                <div id="midContainer" className={Styles.midContainer}>
                                    <Panel defaultSize={70} minSize={50}>
                                        <PanelGroup direction='vertical'>
                                            <Panel defaultSize={70} minSize={50}>
                                                <PanelGroup direction='horizontal'>
                                                    <Panel className={Styles.dbStyle} style={{ overflowY: 'scroll', overflowX: 'hidden' }} defaultSize={30} minSize={25} maxSize={30}>
                                                        {(true) && (
                                                            <>
                                                                <div id="sidebarContent" className={Styles.sidebarContent}>
                                                                    <div className={Styles.databaseHeader}>
                                                                        <FaDatabase size={20} />
                                                                        <span>{props.t('Labs.Nodes.NodeSQL.table.database')}</span>
                                                                    </div>
                                                                    <div className={Styles.tableList}>
                                                                        {tableInfo.map(({ tableName, fields }) => (
                                                                            <div key={tableName} className={Styles.tableItemContainer}>
                                                                                <div
                                                                                    className={Styles.tableItem}
                                                                                    onClick={() => toggleTableExpand(tableName)}
                                                                                >
                                                                                    {expandedTables[tableName] ? (
                                                                                        <KeyboardArrowDown size={16} />
                                                                                    ) : (
                                                                                        <ChevronRight size={16} />
                                                                                    )}
                                                                                    <FaTable size={16} />
                                                                                    <span title={tableName} className={Styles.tableName}>{tableName}</span>
                                                                                </div>
                                                                                <Collapse in={expandedTables[tableName]}>
                                                                                    <div className={Styles.columnList}>
                                                                                        {fields.map((column, index) => (
                                                                                            <div key={index} className={Styles.columnItem}>
                                                                                                <span title={column.name} className={Styles.columnName}>{column.name}</span>
                                                                                                <span className={Styles.columnType}>{column.type}</span>
                                                                                            </div>
                                                                                        ))}
                                                                                    </div>
                                                                                </Collapse>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            </>
                                                        )}
                                                    </Panel>
                                                    <PanelResizeHandle className={Styles.resizePanelHorizontal}>
                                                        <MoreVertSharp fontSize='medium' />
                                                    </PanelResizeHandle>
                                                    <Panel defaultSize={75} minSize={20}>
                                                        <div id="editorContainer" className={Styles.editorContainer}>
                                                            <div id="buttonContainer" className={Styles.bottomLeft}>
                                                                <AivagamButton
                                                                    id="runQuery"
                                                                    onClick={exec}
                                                                    disabled={!query.length}
                                                                    buttonText={<span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{props.t('Labs.Nodes.NodeSQL.button.run')} <PlayArrow fontSize='small' sx={{ ml: '0.25em' }} /> </span>}
                                                                    classStyles={{ boxShadow: '0 3px 6px rgba(0, 0, 0, 0.3)', fontSize: 'small' }}
                                                                />
                                                                <AivagamButton
                                                                    id="verifyQuery"
                                                                    onClick={handleVerify}
                                                                    disabled={!isExecuted}
                                                                    buttonText={<span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{props.t('Labs.Nodes.NodeSQL.button.verify')} <PlayArrow fontSize='small' sx={{ ml: '0.25em' }} /> </span>}
                                                                    classStyles={{ boxShadow: '0 3px 6px rgba(0, 0, 0, 0.3)', fontSize: 'small' }}
                                                                />
                                                            </div>
                                                            <div
                                                                id="editorWindow"
                                                                className={Styles.editorWindow}
                                                            >
                                                                <MonacoEditor
                                                                    value={query}
                                                                    theme="vs-light"
                                                                    width={showTables ? "42vw" : "45vw"}
                                                                    height="88.2vh"
                                                                    defaultLanguage={'sql'}
                                                                    defaultValue={props.t('Labs.Nodes.NodeSQL.monaco')}
                                                                    onChange={(value, event) => { setQuery(value); setIsExecuted(false) }}
                                                                    options={{
                                                                        minimap: {
                                                                            enabled: false,
                                                                        },
                                                                        scrollbar: {
                                                                            vertical: 'hidden',
                                                                            alwaysConsumeMouseWheel: false,
                                                                        },
                                                                        fontSize: 16,
                                                                        wordWrap: 'on',
                                                                        glyphMargin: false,
                                                                        folding: false,
                                                                        lineDecorationsWidth: 10,
                                                                        lineNumbersMinChars: 2,
                                                                        glyphMarginDecorationLaneCount: 0,
                                                                        overviewRulerLanes: 0,
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                    </Panel>
                                                </PanelGroup>
                                            </Panel>
                                            <PanelResizeHandle className={Styles.resizePanelVertical}>
                                                <MoreHorizSharp fontSize='medium' />
                                            </PanelResizeHandle>
                                            <Panel id="outputPane" style={{ height: '100%', width: '100%', overflow: 'scroll' }} defaultSize={30} minSize={25} maxSize={30} collapsible={true} collapsedSize={5}>
                                                <OutputPane
                                                    results={results}
                                                    open={outputOpen}
                                                    t={props.t} />
                                            </Panel>
                                        </PanelGroup>
                                    </Panel>
                                    <Panel ref={instructPanelRef} defaultSize={30} maxSize={30} collapsible={true} onCollapse={() => { setInstructOpen(false) }} onExpand={() => { setInstructOpen(true) }} collapsedSize={5}>
                                        <div className={Styles.innerCont} style={{ overflow: 'auto' }}>
                                            <div data-testid="nodeSql-innerCont-infoContainer" id="infoContainer" className={Styles.infoContainer}>
                                            <InstructionsPane
                                                    steps={steps}
                                                    isMuted={isMuted}
                                                    currentStep={currentStep}
                                                    handleVoiceClick={handleVoiceClick}
                                                    nodeInfo={nodeInfo}
                                                    open={instructOpen}
                                                    onToggle={(next) => {
                                                        if (next) instructPanelRef.current?.expand();
                                                        else instructPanelRef.current?.collapse();
                                                    }}
                                                    t={props.t}
                                                />
                                            </div>
                                        </div>
                                    </Panel>
                                </div>
                            </PanelGroup>
                        </div>

                        <RevealAnswerKey
                            showAnswerKeyDialog={showReveal}
                            setShowAnswerKeyDialog={setShowReveal}
                            steps={steps}
                            currentStep={currentStep}
                            language={"sql"}
                            nodeId={node_id}
                            nodeName={nodeInfo?.name}
                            labTitle={nodeInfo?.labName}
                        />
                        {(!startLab && !nodeFinished && nodeInfo.is_assessment !== 1 && nodeInfo?.resource?.config?.chatbotTopic) && (
                            <HelperChat
                                active={props.aiChat}
                                topic={nodeInfo?.resource?.config?.chatbotTopic}
                            />
                        )}

                        {nodeFinished && <Feedback isInNode={true} nodeName={nodeInfo.name} labName={nodeInfo.labName} />}
                    </>
                )
                    : (
                        <div>
                            <LoadingBackdrop isOpen={true} />
                        </div>
                    )
            }
        </>
    )
}

